import React, { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react';
import { useLocation } from '@tanstack/react-router';
import { CommandDefinition } from '../utils/commands';
import { useI18n } from '../hooks/useI18n';

export interface Command {
    id: string;
    label: string;
    description?: string;
    icon?: React.ReactNode;
    action: () => void;
    shortcut?: string;
    group?: string;
    contextual?: boolean;
    keywords?: string[];
    score?: number;  // For ranking contextual commands higher
    showWhen?: (location: Location) => boolean;  // Function to determine if command should show
}

interface CommandRegistryContextType {
    commands: Command[];
    registerCommand: (command: Command) => void;
    unregisterCommand: (commandId: string) => void;
    registerCommands: (commands: Command[]) => void;
    registerCommandDefinitions: (definitions: CommandDefinition[]) => void;
    setSearchQuery: (query: string) => void;
}

const CommandRegistryContext = createContext<CommandRegistryContextType | undefined>(undefined);

export function CommandRegistryProvider({ children }: { children: ReactNode }) {
    const [commands, setCommands] = useState<Command[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const location = useLocation();
    const { t } = useI18n();

    const registerCommand = useCallback((command: Command) => {
        setCommands((prev) => {
            if (prev.some((c) => c.id === command.id)) {
                return prev;
            }
            return [...prev, command];
        });
    }, []);

    const unregisterCommand = useCallback((commandId: string) => {
        setCommands((prev) => prev.filter((c) => c.id !== commandId));
    }, []);

    const registerCommands = useCallback((newCommands: Command[]) => {
        setCommands((prev) => {
            const existingIds = new Set(prev.map(c => c.id));
            const uniqueNewCommands = newCommands.filter(c => !existingIds.has(c.id));
            return [...prev, ...uniqueNewCommands];
        });
    }, []);

    const registerCommandDefinitions = useCallback((definitions: CommandDefinition[]) => {
        const commands: Command[] = definitions.map(def => ({
            id: def.id,
            label: t(def.labelKey, def.namespace),
            description: def.descriptionKey ? t(def.descriptionKey, def.namespace) : undefined,
            icon: def.icon,
            action: def.action,
            shortcut: def.shortcut,
            group: def.group,
            contextual: def.contextual,
            keywords: def.keywords,
            showWhen: def.showWhen,  // Store the showWhen function
        }));
        registerCommands(commands);
    }, [t, registerCommands]);

    // Filter and rank commands based on context
    const contextualCommands = useMemo(() => {
        const currentPath = location.pathname;
        const isSearching = searchQuery.trim().length > 0;

        return commands
            .map(cmd => {
                let score = 0;
                let shouldShow = true;

                // If command is contextual, check if it should be shown
                if (cmd.contextual) {
                    // If showWhen function exists, use it
                    if (cmd.showWhen) {
                        const matchesContext = cmd.showWhen(location as any);
                        // Show if searching OR if it matches context
                        shouldShow = isSearching || matchesContext;
                        score = matchesContext ? 100 : 0;
                    } else {
                        // Default: check if command ID matches current path
                        // e.g., 'invoices.create' gets high score on /invoices/*
                        const commandPath = cmd.id.split('.')[0]; // Get 'invoices' from 'invoices.create'
                        const isOnMatchingPage = currentPath.startsWith(`/${commandPath}`);


                        // Show if searching OR if on matching page
                        shouldShow = isSearching || isOnMatchingPage;
                        score = isOnMatchingPage ? 100 : 0;
                    }
                }

                return { cmd, score, shouldShow };
            })
            .filter(({ shouldShow }) => shouldShow) // Filter out contextual commands not in context (unless searching)
            .map(({ cmd, score }) => ({ ...cmd, score }))
            .sort((a, b) => (b.score || 0) - (a.score || 0));
    }, [commands, location, searchQuery]);

    return (
        <CommandRegistryContext.Provider value={{
            commands: contextualCommands,
            registerCommand,
            unregisterCommand,
            registerCommands,
            registerCommandDefinitions,
            setSearchQuery
        }}>
            {children}
        </CommandRegistryContext.Provider>
    );
}

export function useCommandRegistry() {
    const context = useContext(CommandRegistryContext);
    if (context === undefined) {
        throw new Error('useCommandRegistry must be used within a CommandRegistryProvider');
    }
    return context;
}

export function useRegisterCommands(commands: Command[]) {
    const { registerCommands, unregisterCommand } = useCommandRegistry();

    React.useEffect(() => {
        registerCommands(commands);
        return () => {
            commands.forEach((c) => unregisterCommand(c.id));
        };
    }, [commands, registerCommands, unregisterCommand]);
}

export function useRegisterCommandDefinitions(definitions: CommandDefinition[]) {
    const { registerCommandDefinitions, unregisterCommand } = useCommandRegistry();

    React.useEffect(() => {
        registerCommandDefinitions(definitions);
        return () => {
            definitions.forEach((def) => unregisterCommand(def.id));
        };
    }, [definitions, registerCommandDefinitions, unregisterCommand]);
}
